import React from 'react';
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

// Styled wrapper for consistent styling
const StyledWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
  },
}));

/**
 * MagicDatePicker - Enhanced date/time picker component
 * 
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {Date|string} props.value - Current date value
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.type - Picker type: 'date', 'datetime', or 'time'
 * @param {Object} props.pickerProps - Additional props for the picker component
 * @param {Object} props.textFieldProps - Additional props for the TextField
 * @param {boolean} props.error - Whether the field has an error
 * @param {string} props.helperText - Helper text to display
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.fullWidth - Whether the field should take full width
 * @param {string} props.minDate - Minimum selectable date
 * @param {string} props.maxDate - Maximum selectable date
 * @param {Function} props.renderInput - Custom render function for input
 */
const MagicDatePicker = ({
  name,
  label,
  value,
  onChange,
  type = 'date',
  pickerProps = {},
  textFieldProps = {},
  error = false,
  helperText = '',
  required = false,
  fullWidth = true,
  minDate,
  maxDate,
  renderInput,
  ...props
}) => {
  // Handle date change
  const handleChange = (date) => {
    if (onChange) {
      onChange(date, name);
    }
  };

  // Choose the appropriate picker component based on type
  const PickerComponent = type === 'datetime' 
    ? DateTimePicker 
    : type === 'time' 
      ? TimePicker 
      : DatePicker;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledWrapper>
        <PickerComponent
          label={label}
          value={value || null}
          onChange={handleChange}
          minDate={minDate}
          maxDate={maxDate}
          slotProps={{
            textField: {
              name,
              required,
              error,
              helperText,
              fullWidth,
              ...textFieldProps,
              ...props.InputProps
            }
          }}
          {...pickerProps}
          {...props}
        />
      </StyledWrapper>
    </LocalizationProvider>
  );
};

export default MagicDatePicker;